import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ReferenceAdapter } from '../reference.adapter';
import { ScopeJobDefinition, SynapseScopeJobDefinitionActivity, A365BackendModel, ScopeCapacityAllocation } from 'shell-models-lib';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SynapseScopeJobDefinitionActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            scopeJob: '*',
            scriptParameters: '*',
            priority: '*',
            maxUnavailability: '*',
            tags: '*',
            nebulaArguments: '*',
            token: '*',
            tokenPercentage: '*',
            ecoToken: '*',
            ecoTokenPercentage: '*'
        }
    }
})
export class SynapseScopeJobDefinitionActivityAdapter extends ActivityAsyncAdapter<SynapseScopeJobDefinitionActivity, A365BackendModel.SynapseScopeJobDefinitionActivity> {

    protected async _toResource(activity: SynapseScopeJobDefinitionActivity): Promise<A365BackendModel.SynapseScopeJobDefinitionActivity> {
        const modelScopeJobDefinitionActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(SynapseScopeJobDefinitionActivity).name) as A365BackendModel.SynapseScopeJobDefinitionActivity;

        modelScopeJobDefinitionActivity.typeProperties = {
            scopeJob: this._getJobDefinitionReference(activity.scopeJobDefinition),
            scriptParameters: toParameterObject(activity.scriptParameters),
            priority: ValueAdapter.toResource(activity, SynapseScopeJobDefinitionActivity.PRIORITY),
            maxUnavailability: ValueAdapter.toResource(activity, SynapseScopeJobDefinitionActivity.MAX_UNAVAILABLITY),
            tags: toParameterObject(activity.tags),
            nebulaArguments: ValueAdapter.toResource(activity, SynapseScopeJobDefinitionActivity.NEBULA_ARGUMENTS)
        };

        switch (activity.standardCapacityAllocation) {
            case ScopeCapacityAllocation.AbsoluteNumber:
                modelScopeJobDefinitionActivity.typeProperties.token = activity.standardCustomValue;
                break;
            case ScopeCapacityAllocation.Percentage:
                modelScopeJobDefinitionActivity.typeProperties.tokenPercentage = activity.standardCustomValue;
                break;
            default:
                break;
        }

        switch (activity.ecoCapacityAllocation) {
            case ScopeCapacityAllocation.AbsoluteNumber:
                modelScopeJobDefinitionActivity.typeProperties.ecoToken = activity.ecoCustomValue;
                break;
            case ScopeCapacityAllocation.Percentage:
                modelScopeJobDefinitionActivity.typeProperties.ecoTokenPercentage = activity.ecoCustomValue;
                break;
            default:
                break;
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelScopeJobDefinitionActivity, parsed);
        }

        return modelScopeJobDefinitionActivity;
    }

    protected async _toModel(modelActivity: A365BackendModel.SynapseScopeJobDefinitionActivity): Promise<SynapseScopeJobDefinitionActivity> {
        const activity: SynapseScopeJobDefinitionActivity = SynapseScopeJobDefinitionActivity.MODEL.create<SynapseScopeJobDefinitionActivity>(modelActivity.name, modelActivity.description);

        this._updatedCommonProperties(modelActivity, activity);

        activity.scopeJobDefinition = ReferenceAdapter.toProxyModel<ScopeJobDefinition>(
            Metamodel.modelFor(ScopeJobDefinition),
            modelActivity.typeProperties.scopeJob, this.context);

        activity.scriptParameters = toParameterClass(modelActivity.typeProperties.scriptParameters, activity);
        ValueAdapter.toModel(activity, SynapseScopeJobDefinitionActivity.PRIORITY, modelActivity.typeProperties.priority);
        ValueAdapter.toModel(activity, SynapseScopeJobDefinitionActivity.MAX_UNAVAILABLITY, modelActivity.typeProperties.maxUnavailability);
        activity.tags = toParameterClass(modelActivity.typeProperties.tags, activity);
        ValueAdapter.toModel(activity, SynapseScopeJobDefinitionActivity.NEBULA_ARGUMENTS, modelActivity.typeProperties.nebulaArguments);

        if (modelActivity.typeProperties.token) {
            activity.standardCapacityAllocation = ScopeCapacityAllocation.AbsoluteNumber;
            activity.standardCustomValue = modelActivity.typeProperties.token;
        } else if (modelActivity.typeProperties.tokenPercentage) {
            activity.standardCapacityAllocation = ScopeCapacityAllocation.Percentage;
            activity.standardCustomValue = modelActivity.typeProperties.tokenPercentage;
        } else if (modelActivity.typeProperties.ecoToken) {
            activity.ecoCapacityAllocation = ScopeCapacityAllocation.AbsoluteNumber;
            activity.ecoCustomValue = modelActivity.typeProperties.ecoToken;
        } else if (modelActivity.typeProperties.ecoTokenPercentage) {
            activity.ecoCapacityAllocation = ScopeCapacityAllocation.Percentage;
            activity.ecoCustomValue = modelActivity.typeProperties.ecoTokenPercentage;
        } else {
            activity.standardCapacityAllocation = ScopeCapacityAllocation.Default;
        }

        return activity;
    }

    private _getJobDefinitionReference(named: ScopeJobDefinition): Model.ArtifactReference {
        if (!named) {
            return undefined;
        }

        return ReferenceAdapter.toResource(named, this.context) as Model.ArtifactReference;
    }

}
